import { Disclosure } from "@headlessui/react";
import moment from "moment";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { getAllVirtualClassesForStudentRTQ } from "../../api/subject";
import Partials from "../partials";
import { joinMeeting } from "../../api/meetings";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from "react-toastify";
import Toggle from "../../assets/images/modal-toggle.svg";
import _ from "lodash";
import { isJoinReady } from "../../utils/time";

const TabAction = {
  TODAY: "today",
  TOMORROW: "tomorrow",
  ALL: "all",
};

const BlockStudentLiveClass = () => {
  const { keycloak } = useKeycloak();
  const [selectedRow, _selectedRow] = useState(0);
  const joiningClassIdRef = useRef(null);
  const [activeTab, setActiveTab] = useState(TabAction.TODAY);
  const [filterData, setFilterData] = useState([]);
  const {
    isLoading,
    isError,
    data: liveClasses,
    error,
  } = useQuery(
    ["allVirtualClassesForStudent"],
    getAllVirtualClassesForStudentRTQ
  );

  const {
    mutate,
    isLoading: isJoiningMeeting,
    isError: isJoinMeetingError,
    data: meetingData,
    error: meetingError,
  } = useMutation(joinMeeting, {
    onSuccess: (data) => {
      if (data?.data?.body?.join_url) {
        let joinUrl = data.data.body.join_url;
        // Append buddingClassId as a URL fragment (#) so it doesn't affect BBB's checksum validation
        // Fragments are never sent to the server, but the BBB frontend JS can read them via window.location.hash
        if (joiningClassIdRef.current) {
          joinUrl = `${joinUrl}#buddingClassId=${encodeURIComponent(joiningClassIdRef.current)}`;
          console.log(`[BuddingFrontend] Appended buddingClassId=${joiningClassIdRef.current} to join_url (as fragment)`);
        }
        window.open(joinUrl, "_self");
      } else {
        toast.error("We were unable to get join url. Please try again.");
      }
    },
  });

  const joinClass = (classId, index) => {
    if (!keycloak?.tokenParsed) {
      return;
    }

    const student = keycloak?.tokenParsed;
    const payload = {
      id: student.sub,
      first_name: student.given_name,
      last_name: student.family_name,
      mobile_number: student.phone_number,
      photo: student.photo,
      email: student.email,
    };

    joiningClassIdRef.current = classId;
    mutate({ classId, payload });
    _selectedRow(index);
  };

  //filter the date ascending order
  const showAllFilters = () => {
    const classData = liveClasses?.data;
    const sortedDatesAsc = _.sortBy(classData, (item) =>
      moment.unix(item?.start_time).date()
    );

    if (sortedDatesAsc?.length > 0) {
      const modifyData = sortedDatesAsc.filter((item) => {
        if (item?.student_count > 0) {
          return item;
        }
      });
      setFilterData(modifyData);
    }
  };

  //filter the today class
  const todayClass = (isInitialLoading) => {
    const classData = liveClasses?.data;
    const currentDate = moment().date();
    const modifyData = classData?.filter((item) => {
      if (
        moment.unix(item?.start_time).date() === currentDate &&
        item?.student_count > 0
      ) {
        return item;
      }
    });

    if (!modifyData.length && isInitialLoading) {
      tomorrowClass(isInitialLoading);
    } else {
      setFilterData(modifyData);
    }
  };
  //filter the tomorrow class
  const tomorrowClass = (isInitialLoading) => {
    const classData = liveClasses?.data;
    const currentDate = moment().clone().add(1, "d").date();
    const modifyData = classData.filter((item) => {
      if (
        moment.unix(item?.start_time).date() === currentDate &&
        item?.student_count > 0
      ) {
        return item;
      }
    });
    if (!modifyData.length && isInitialLoading) {
      showAllFilters();
    } else {
      setFilterData(modifyData);
      setActiveTab(TabAction.TOMORROW);
    }
  };

  const handleFilter = (filter, isInitialLoading = true) => {
    setActiveTab(filter);
    if (filter === TabAction.ALL) {
      showAllFilters();
    } else if (filter === TabAction.TODAY) {
      todayClass(isInitialLoading);
    } else if (filter === TabAction.TOMORROW) {
      tomorrowClass(isInitialLoading);
    }
  };

  useEffect(() => {
    if (liveClasses?.data?.length) {
      handleFilter(activeTab);
    }
  }, [liveClasses]);

  //below function is used to check the class is live or not
  function checkOneMinuteRemaining(classData) {
    const startDate = moment(classData.start_time * 1000);
    const endDate = moment(classData.end_time * 1000);
    const currentDate = moment(moment().unix() * 1000);

    const classStartTime = moment
      .unix(classData.start_time)
      .subtract(1, "minutes");

    const formattedEndDate = moment(endDate).format("M/D/YYYY");
    const formattedCurrentDate = moment(currentDate).format("M/D/YYYY");

    const timeDuration = classData.duration_seconds / 60;
    const classEndTime = moment(startDate).add(timeDuration, "minutes");

    if (
      currentDate.isSameOrAfter(startDate, "date") &&
      moment(formattedCurrentDate).isSameOrBefore(
        moment(formattedEndDate),
        "dates"
      )
    ) {
      if (
        moment(currentDate, "LT").isSame(
          moment(classStartTime, "LT"),
          "minutes"
        ) &&
        !moment(currentDate, "LT").isAfter(
          moment(classEndTime, "LT"),
          "minutes"
        )
      ) {
        return false;
      } else if (
        moment(currentDate, "LT").isSame(moment(classEndTime, "LT"), "minutes")
      ) {
        return true;
      } else if (
        moment(currentDate, "LT").isAfter(
          moment(classStartTime, "LT"),
          "minutes"
        ) &&
        !moment(currentDate, "LT").isAfter(
          moment(classEndTime, "LT"),
          "minutes"
        )
      ) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  return (
    <>
      <div className="mt-4 mb-3 flex justify-start items-center gap-3">
        <button
          onClick={() => handleFilter(TabAction.TODAY, false)}
          className={`${
            activeTab === TabAction.TODAY &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          Today
        </button>
        <button
          onClick={() => handleFilter(TabAction.TOMORROW, false)}
          className={`${
            activeTab === TabAction.TOMORROW &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          Tomorrow
        </button>
        <button
          onClick={() => handleFilter(TabAction.ALL)}
          className={`${
            activeTab === TabAction.ALL &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          All
        </button>
      </div>
      <div className="font-['Poppins'] mx-auto text-lg mt-4">
  {filterData?.length}{" "}
  {filterData?.length === 1
    ? activeTab === TabAction.TODAY
      ? "class scheduled for today"
      : activeTab === TabAction.TOMORROW
      ? "class scheduled for tomorrow"
      : "class"
    : activeTab === TabAction.TODAY
    ? "classes scheduled for today"
    : activeTab === TabAction.TOMORROW
    ? "classes scheduled for tomorrow"
    : "classes"}
  .
</div>
      {isLoading ? (
        <Partials.PartialLoading />
      ) : (
        filterData?.map((vClass, index) => (
          <div className="rounded-lg mt-5" key={index}>
            <div className={"w-full"}>
              <div
                className={
                  "w-full bg-white mx-auto text-left shadow rounded-lg  mb-0 text-lg flex flex-col"
                }
              >
                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="py-2">
                        <div
                          className={
                            open
                              ? "classBox grid grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 p-2 text-sm"
                              : `grid grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 p-2 text-sm`
                          }
                        >
                          <span className="grid grid-cols-2 text-sm">
                            <span className="mr-3  min-w-lg  text-sm md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-user text-2xl mr-3"></i>
                              <span className="break-all text-sm">{vClass.title}</span>
                            </span>
                            <span className="mr-3 max-w-xs text-sm md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-book-open text-2xl mr-3"></i>
                              <p className="truncate text-sm">
                                {vClass?.subject?.title}
                              </p>
                            </span>
                          </span>

                          <span className="grid grid-cols-2">
                            <span className="mr-3 min-w-lg text-sm md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-clock text-2xl mr-3"></i>
                              <span className="text-sm">
                                {moment
                                  .unix(vClass.start_time)
                                  .format("HH:mmA")}
                                -
                                {moment
                                  .unix(
                                    vClass.start_time + vClass.duration_seconds
                                  )
                                  .format("HH:mmA")}
                              </span>
                            </span>
                            <span className="min-w-lg text-sm md:text-lg flex items-center gap-2">
                              <i className="text-zinc-400 icon icon-users text-2xl mr-3"></i>
                              <span className="text-sm">
                                {vClass?.student_count}
                              </span>
                              <button
                                type="button"
                                // disabled={
                                //   isJoiningMeeting ||
                                //   isJoinReady(
                                //     vClass.start_time,
                                //     vClass.end_time,
                                //     vClass.repeats
                                //   )
                                // }
                                disabled={checkOneMinuteRemaining(vClass)}
                                onClick={() =>
                                  !isJoiningMeeting &&
                                  joinClass(vClass.id, index)
                                }
                                // className="bg-black text-white text-sm rounded-lg flex ml-auto  py-2 px-4 border-none outline-0"
                                className={`bg-${
                                  checkOneMinuteRemaining(vClass)
                                    ? "gray-300 text-black "
                                    : "black text-white"
                                } text-sm rounded-lg flex px-4 py-2 border-none outline-0 mr-4`}
                              >
                                {selectedRow === index && isJoiningMeeting && (
                                  <Partials.PartialLoading />
                                )}
                                Join
                              </button>
                              <span className="">
                                <img
                                  src={Toggle}
                                  alt=""
                                  className={
                                    !open
                                      ? "mx-4 h-4 w-4 rotate-180"
                                      : "mx-4 h-4 w-4"
                                  }
                                />
                              </span>
                            </span>
                          </span>
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-gray-500">
                        <div className="px-3 py-1">
                          <div className="flex flex-row justify-between p-2">
                            <span className="text-sm">Details</span>
                            <span className="text-sm">
                              {/*<button*/}
                              {/*    type="button"*/}
                              {/*    onClick={() => history.push(`/create-virtual-class/${vClass.id}/edit`)}*/}
                              {/*>*/}
                              {/*    <i className="text-zinc-400 icon icon-edit-2"></i>*/}
                              {/*</button>*/}
                            </span>
                          </div>
                          <div className="p-2">
                            <div className="text-xs text-black text-left pl-2">
                              {parse(vClass.description)}
                            </div>
                            {/* <div className="text-right mt-2">
                                <button
                                  type="button"
                                  disabled={isJoiningMeeting}
                                  onClick={() =>
                                    !isJoiningMeeting &&
                                    joinClass(vClass.id, index)
                                  }
                                  className="bg-black text-white text-sm rounded-lg flex ml-auto  py-2 px-4 border-none outline-0"
                                >
                                  {selectedRow === index &&
                                    isJoiningMeeting && (
                                      <Partials.PartialLoading />
                                    )}
                                  Join
                                </button>
                              </div> */}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>
            </div>
          </div>
        ))
      )}
    </>
  );
};

export default BlockStudentLiveClass;
